import { useEffect, useRef } from "react";
import { Box, chakra, Flex } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useReq } from "@larner.dev/use-req";
import { useNavigate, useNavigationType } from "react-router-dom";
import type { UserProfile } from "@greenflagdate/shared";

import { apiReq } from "../../../lib/apiReq";
import { useStoreData } from "../../../lib/store/store";
import { LoaderScreen } from "../../../components/LoaderScreen/LoaderScreen";
import { Icon } from "../../../components/Icon/Icon";
import { MatchProfile } from "../Match/MatchProfile";

const MChakraDiv = motion.create(chakra.div);

const modalVariants = {
  initial: { opacity: 0, scale: 0.92 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" as const },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: { duration: 0.15, ease: "easeIn" as const },
  },
};

export const Profile = () => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const storeData = useStoreData();
  const userId = storeData.user?.id;
  const [profileReq, profileReqState] = useReq<UserProfile>(apiReq);
  const isColdLoadRef = useRef(navigationType === "POP");
  const isColdLoad = isColdLoadRef.current;

  useEffect(() => {
    if (userId) {
      profileReq.get(`/user/profile/${userId}`);
    }
  }, [profileReq, userId]);

  return (
    <MChakraDiv
      key="profile-modal"
      initial={isColdLoad ? false : "initial"}
      animate="animate"
      exit="exit"
      variants={modalVariants}
      style={{
        position: "fixed",
        inset: 0,
        background: "white",
        zIndex: 30,
        display: "flex",
        flexDirection: "column",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        transformOrigin: "center",
      }}
    >
      <Flex direction="column" flex="1" minH="0" bg="bg.canvas">
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          h="56px"
          px="4"
          bg="bg.canvas"
          zIndex="2"
          flexShrink={0}
        >
          <chakra.button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            bg="transparent"
            border="none"
            cursor="pointer"
            color="fg.default"
            display="flex"
            alignItems="center"
            gap="1"
            fontSize="sm"
            fontWeight="semibold"
            p="2"
            ml="-2"
          >
            <Icon name="chevron-left" boxSize="14px" />
            BACK
          </chakra.button>
          <chakra.h1
            fontSize="lg"
            fontWeight="semibold"
            m="0"
            position="absolute"
            left="50%"
            top="50%"
            transform="translate(-50%, -50%)"
          >
            Profile
          </chakra.h1>
          <chakra.button
            type="button"
            onClick={() => navigate("/settings/profile")}
            aria-label="Edit profile"
            bg="transparent"
            border="none"
            cursor="pointer"
            color="fg.default"
            display="flex"
            alignItems="center"
            p="2"
            mr="-2"
          >
            <Icon name="pen-to-square" boxSize="20px" />
          </chakra.button>
        </Box>

        <Box flex="1" minH="0" overflowY="auto" bg="bg.canvas">
          {!profileReqState.result ? (
            <LoaderScreen />
          ) : (
            <MatchProfile profile={profileReqState.result} />
          )}
        </Box>
      </Flex>
    </MChakraDiv>
  );
};
